/**
 * useCallPlugins Hook
 * Handles Android/iOS-specific call plugins:
 * - ProximitySensor: Turn off screen when phone near ear during voice calls
 * - PictureInPicture: Allow video calls in PiP mode when app is backgrounded
 * - ScreenOrientation: Lock screen orientation during calls
 */

'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import { isCapacitor, isAndroid, isIOS } from '@/lib/platformUtils';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import type { ProximitySensorPlugin } from '@/plugins/ProximitySensor';
import type { AudioRouterPlugin } from '@/plugins/AudioRouter';
import type { PictureInPicturePlugin } from '@/plugins/PictureInPicture';
import type { ScreenOrientationPlugin } from '@/plugins/ScreenOrientation';
import type { PhoneStatePlugin } from '@/plugins/PhoneState';
import type { KeepAwakePlugin } from '@capacitor-community/keep-awake';

// Plugin references - loaded dynamically
let ProximitySensor: ProximitySensorPlugin | null = null;
let AudioRouter: AudioRouterPlugin | null = null;
let PictureInPicture: PictureInPicturePlugin | null = null;
let ScreenOrientation: ScreenOrientationPlugin | null = null;
let KeepAwake: KeepAwakePlugin | null = null;
let PhoneState: PhoneStatePlugin | null = null;

// Track if plugins are initialized
let pluginsInitialized = false;

// Initialize plugins on module load
async function initializePlugins() {
  if (pluginsInitialized) return;
  
  if (!isCapacitor()) {
    pluginsInitialized = true;
    return;
  }
  
  try {
    // Proximity sensor - Android and iOS (turns off screen when phone near ear)
    if (isAndroid() || isIOS()) {
      const psModule = await import('@/plugins/ProximitySensor');
      ProximitySensor = psModule.ProximitySensor;
    }

    // AudioRouter - Android only, used to drop loudspeaker to earpiece when
    // the phone is held to the ear mid-call
    if (isAndroid()) {
      const arModule = await import('@/plugins/AudioRouter');
      AudioRouter = arModule.AudioRouter;
    }
    
    // Picture-in-Picture - Android 8+ and iOS
    const pipModule = await import('@/plugins/PictureInPicture');
    PictureInPicture = pipModule.PictureInPicture;
    
    // Screen Orientation - all mobile platforms
    const soModule = await import('@/plugins/ScreenOrientation');
    ScreenOrientation = soModule.ScreenOrientation;
    
    // KeepAwake - prevents screen from sleeping during calls
    const kaModule = await import('@capacitor-community/keep-awake');
    KeepAwake = kaModule.KeepAwake;
    
    // PhoneState - detects if user is on a cellular call (busy detection)
    const psPhoneModule = await import('@/plugins/PhoneState');
    PhoneState = psPhoneModule.PhoneState;
    // Start listening for phone state changes
    try {
      await PhoneState.startListening();
    } catch (e2) {}
    
  } catch (e) {}
  
  pluginsInitialized = true;
}

// Initialize on module load
if (typeof window !== 'undefined') {
  initializePlugins();
}

interface UseCallPluginsOptions {
  isInCall?: boolean;
  isVideoCall?: boolean;
  onPiPEnter?: () => void;
  onPiPExit?: () => void;
}

interface UseCallPluginsReturn {
  // Proximity sensor
  isProximityActive: boolean;
  enableProximitySensor: () => Promise<void>;
  disableProximitySensor: () => Promise<void>;
  
  // Picture-in-Picture
  isPiPSupported: boolean;
  isPiPActive: boolean;
  enterPiP: () => Promise<boolean>;
  exitPiP: () => Promise<void>;
  
  // Screen orientation
  lockOrientation: (orientation: 'portrait' | 'landscape') => Promise<void>;
  unlockOrientation: () => Promise<void>;
  
  // KeepAwake
  isKeepAwakeActive: boolean;
  
  // Busy detection (PhoneState)
  checkIfBusy: () => Promise<{ isBusy: boolean; reason: 'cellular' | 'none'; message?: string }>;
}

export function useCallPlugins(options: UseCallPluginsOptions = {}): UseCallPluginsReturn {
  const { isInCall = false, isVideoCall = false, onPiPEnter, onPiPExit } = options;
  
  // State
  const [isProximityActive, setIsProximityActive] = useState(false);
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const [isPiPActive, setIsPiPActive] = useState(false);
  const [isKeepAwakeActive, setIsKeepAwakeActive] = useState(false);
  
  // Refs
  const appStateListenerRef = useRef<PluginListenerHandle | null>(null);
  
  /**
   * Initialize PiP support check
   */
  useEffect(() => {
    if (!isCapacitor() || !PictureInPicture) return;
    
    PictureInPicture.isSupported()
      .then((result: { supported: boolean }) => {
        setIsPiPSupported(result.supported);
      })
      .catch(() => setIsPiPSupported(false));
  }, []);
  
  /**
   * Enable proximity sensor - turns off screen when phone near ear
   * Only for voice calls on Android
   */
  const enableProximitySensor = useCallback(async () => {
    if (!isCapacitor() || !ProximitySensor || isProximityActive) return;
    
    try {
      await ProximitySensor.enable();
      setIsProximityActive(true);
      console.log('[useCallPlugins] Proximity sensor enabled - screen will turn off when near ear');
    } catch (e) {
      console.log('[useCallPlugins] Failed to enable proximity sensor:', e);
    }
  }, [isProximityActive]);
  
  /**
   * Disable proximity sensor
   */
  const disableProximitySensor = useCallback(async () => {
    if (!isCapacitor() || !ProximitySensor || !isProximityActive) return;
    
    try {
      await ProximitySensor.disable();
      setIsProximityActive(false);
      console.log('[useCallPlugins] Proximity sensor disabled');
    } catch (e) {
      console.log('[useCallPlugins] Failed to disable proximity sensor:', e);
    }
  }, [isProximityActive]);
  
  /**
   * Enter Picture-in-Picture mode
   */
  const enterPiP = useCallback(async (): Promise<boolean> => {
    if (!PictureInPicture || !isPiPSupported) return false;
    
    try {
      await PictureInPicture.enter({
        aspectRatio: { width: 9, height: 16 },
      });
      setIsPiPActive(true);
      onPiPEnter?.();
      console.log('[useCallPlugins] Entered PiP mode');
      return true;
    } catch (e) {
      console.log('[useCallPlugins] Failed to enter PiP:', e);
      return false;
    }
  }, [isPiPSupported, onPiPEnter]);
  
  /**
   * Exit Picture-in-Picture mode
   */
  const exitPiP = useCallback(async () => {
    if (!PictureInPicture || !isPiPActive) return;
    
    try {
      await PictureInPicture.exit();
      setIsPiPActive(false);
      onPiPExit?.();
      console.log('[useCallPlugins] Exited PiP mode');
    } catch (e) {
      console.log('[useCallPlugins] Failed to exit PiP:', e);
    }
  }, [isPiPActive, onPiPExit]);
  
  /**
   * Lock screen orientation
   */
  const lockOrientation = useCallback(async (orientation: 'portrait' | 'landscape') => {
    if (!ScreenOrientation) return;
    
    try {
      await ScreenOrientation.lock({ orientation });
      console.log('[useCallPlugins] Orientation locked:', orientation);
    } catch (e) {
      console.log('[useCallPlugins] Failed to lock orientation:', e);
    }
  }, []);
  
  /**
   * Unlock screen orientation
   */
  const unlockOrientation = useCallback(async () => {
    if (!ScreenOrientation) return;
    
    try {
      await ScreenOrientation.unlock();
      console.log('[useCallPlugins] Orientation unlocked');
    } catch (e) {
      console.log('[useCallPlugins] Failed to unlock orientation:', e);
    }
  }, []);
  
  /**
   * Auto-enable proximity sensor for voice calls
   * While enabled, proximityChanged also moves audio: near ear drops the
   * loudspeaker to the earpiece, moving away restores the previous route.
   */
  useEffect(() => {
    let isCancelled = false;
    let proximityListener: { remove: () => Promise<void> } | null = null;
    let speakerBeforeNear = true;
    let lastNear = false;

    const syncProximityState = async () => {
      if (!isInCall || isVideoCall) {
        // Not in call or it's a video call - disable proximity
        if (!isCancelled) {
          await disableProximitySensor();
        }
        return;
      }

      // Voice call - enable proximity sensor
      if (!isCancelled) {
        if (ProximitySensor && !proximityListener) {
          try {
            proximityListener = await ProximitySensor.addListener(
              'proximityChanged',
              ({ near }: { near: boolean }) => {
                lastNear = near;
                if (!AudioRouter || isCancelled) return;
                void (async () => {
                  try {
                    const status = await AudioRouter.getStatus();
                    if (status.bluetooth) return; // headset owns the route
                    if (near) {
                      speakerBeforeNear = status.speaker;
                      await AudioRouter.setSpeakerphone({ speaker: false });
                    } else {
                      await AudioRouter.setSpeakerphone({ speaker: speakerBeforeNear });
                    }
                  } catch {}
                })();
              }
            );
          } catch {}
        }
        await enableProximitySensor();
      }
    };

    void syncProximityState();

    return () => {
      isCancelled = true;
      void proximityListener?.remove();
      void disableProximitySensor();
      // Call ended with the phone at the ear - hand audio back to the
      // route that was active before proximity took over.
      if (lastNear && AudioRouter) {
        void AudioRouter.setSpeakerphone({ speaker: speakerBeforeNear }).catch(() => {});
      }
    };
  }, [isInCall, isVideoCall, enableProximitySensor, disableProximitySensor]);
  
  /**
   * Auto-enter PiP when app is backgrounded during video call (Android only)
   */
  useEffect(() => {
    if (!isInCall || !isVideoCall || !isPiPSupported || !isAndroid()) return;
    
    // Listen for app state changes
    const setupAppStateListener = async () => {
      appStateListenerRef.current = await App.addListener('appStateChange', async (state) => {
        if (!state.isActive && isInCall && isVideoCall) {
          // App going to background during video call - enter PiP
          await enterPiP();
        } else if (state.isActive && isPiPActive) {
          // App coming to foreground - exit PiP
          await exitPiP();
        }
      });
    };
    
    setupAppStateListener();
    
    return () => {
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
        appStateListenerRef.current = null;
      }
    };
  }, [isInCall, isVideoCall, isPiPSupported, isPiPActive, enterPiP, exitPiP]);
  
  /**
   * Auto-enable KeepAwake during calls to prevent screen sleep
   */
  useEffect(() => {
    if (!isInCall || !KeepAwake) return;
    
    KeepAwake.keepAwake()
      .then(() => {
        setIsKeepAwakeActive(true);
        console.log('[useCallPlugins] KeepAwake enabled');
      })
      .catch(() => {});
    
    return () => {
      if (KeepAwake) {
        KeepAwake.allowSleep()
          .then(() => {
            setIsKeepAwakeActive(false);
            console.log('[useCallPlugins] KeepAwake disabled');
          })
          .catch(() => {});
      }
    };
  }, [isInCall]);
  
  /**
   * Check if user is busy on a cellular call (PhoneState plugin)
   */
  const checkIfBusy = useCallback(async (): Promise<{ isBusy: boolean; reason: 'cellular' | 'none'; message?: string }> => {
    if (!PhoneState || !isCapacitor()) {
      return { isBusy: false, reason: 'none' };
    }
    
    try {
      const phoneState = await PhoneState.isOnCall();
      console.log('[useCallPlugins] PhoneState check:', phoneState);
      
      if (phoneState.isOnCall) {
        return {
          isBusy: true,
          reason: 'cellular',
          message: phoneState.reason === 'cellular' ? 'On a phone call' : 'On another call'
        };
      }
    } catch (e) {
      console.log('[useCallPlugins] PhoneState check failed (continuing):', e);
    }
    
    return { isBusy: false, reason: 'none' };
  }, []);
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      // Cleanup proximity sensor
      if (ProximitySensor && isProximityActive) {
        ProximitySensor.disable().catch(() => {});
      }
      
      // Cleanup PiP
      if (PictureInPicture && isPiPActive) {
        PictureInPicture.exit().catch(() => {});
      }
      
      // Cleanup orientation lock
      if (ScreenOrientation) {
        ScreenOrientation.unlock().catch(() => {});
      }
      
      // Cleanup KeepAwake
      if (KeepAwake) {
        KeepAwake.allowSleep().catch(() => {});
      }
      
      // Cleanup PhoneState
      if (PhoneState) {
        try { PhoneState.stopListening(); } catch (_) {}
      }
      
      // Cleanup listeners
      if (appStateListenerRef.current) {
        appStateListenerRef.current.remove();
      }
    };
  }, [isProximityActive, isPiPActive]);
  
  return {
    // Proximity sensor
    isProximityActive,
    enableProximitySensor,
    disableProximitySensor,
    
    // Picture-in-Picture
    isPiPSupported,
    isPiPActive,
    enterPiP,
    exitPiP,
    
    // Screen orientation
    lockOrientation,
    unlockOrientation,
    
    // KeepAwake
    isKeepAwakeActive,
    
    // Busy detection
    checkIfBusy,
  };
}
